import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';
import type {
  CreateProformaInput,
  UpdateProformaInput,
  BatchProformaItemsInput,
} from '@shared/validation/proforma-schemas';
import type {
  ProformaListDTO,
  ProformaDetailDTO,
} from '../../../backend/services/proforma-service';

interface ProformasResponse {
  data: ProformaListDTO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface ProformaFilters {
  search?: string;
  estado?: string;
  projectId?: string;
  clientId?: string;
  year?: number;
  month?: number;
  page?: number;
  limit?: number;
}

// ── List ──
export function useProformas(filters?: ProformaFilters) {
  return useQuery({
    queryKey: queryKeys.proformas.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const response = await api.get<ProformasResponse>('/proformas', {
        params: filters,
      });
      return response.data;
    },
  });
}

// ── Detail ──
export function useProforma(id: string) {
  return useQuery({
    queryKey: queryKeys.proformas.detail(id),
    queryFn: async () => {
      const response = await api.get<{ data: ProformaDetailDTO }>(`/proformas/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

// ── Create ──
export function useCreateProforma() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProformaInput) => {
      const response = await api.post<{ data: { id: string; numero: number } }>(
        `/projects/${data.projectId}/proformas`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.all });
      toast('success', `Proforma #${data.numero} creada exitosamente`);
    },
  });
}

// ── Update metadata ──
export function useUpdateProforma() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateProformaInput }) => {
      const response = await api.put<{ data: unknown }>(`/proformas/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.detail(variables.id) });
      toast('success', 'Proforma actualizada');
    },
  });
}

// ── Batch upsert items ──
export function useBatchUpsertItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ proformaId, data }: { proformaId: string; data: BatchProformaItemsInput }) => {
      const response = await api.post<{ data: { upserted: number } }>(
        `/proformas/${proformaId}/items`,
        data,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.detail(variables.proformaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.all });
      toast('success', `${_data.upserted} ítems guardados`);
    },
  });
}

// ── Delete item ──
export function useDeleteProformaItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ proformaId, itemId }: { proformaId: string; itemId: string }) => {
      const response = await api.delete<{ data: { deleted: boolean } }>(
        `/proformas/${proformaId}/items/${itemId}`,
      );
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.detail(variables.proformaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.all });
    },
  });
}

// ── Transition state ──
export function useTransitionProforma() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      proformaId,
      action,
      motivoRechazo,
    }: {
      proformaId: string;
      action: 'send' | 'send_to_client' | 'approve' | 'reject' | 'reactivate';
      motivoRechazo?: string;
    }) => {
      const response = await api.patch<{
        data: { id: string; estado: string; motivo_rechazo: string | null; fecha_aprobacion: string | null };
      }>(`/proformas/${proformaId}/estado`, { action, motivoRechazo });
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.detail(variables.proformaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: ['project-summary'] });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.summary });
      const msgs: Record<string, string> = {
        send: 'Proforma enviada para aprobación',
        send_to_client: 'Proforma marcada como enviada al cliente',
        approve: 'Proforma aprobada exitosamente',
        reject: 'Proforma rechazada',
        reactivate: 'Proforma reactivada y lista para editar',
      };
      toast('success', msgs[variables.action] ?? 'Estado actualizado');
    },
  });
}

// ── Generate PDF ──
export function useGenerateProformaPdf() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (proformaId: string) => {
      const response = await api.post<{
        data: { version: number; download_url: string; expires_in: number; html?: string };
      }>(`/proformas/${proformaId}/pdf`);
      return response.data.data;
    },
    onSuccess: (_data, proformaId) => {
      queryClient.invalidateQueries({ queryKey: ['proforma-versions', proformaId] });
      toast('success', `PDF versión ${_data.version} generado exitosamente`);
    },
  });
}

// ── Generate XLSX ──
export function useGenerateProformaXlsx() {
  return useMutation({
    mutationFn: async (proformaId: string) => {
      const res = await api.post<{ data: { xlsx_base64: string; filename: string } }>(
        `/proformas/${proformaId}/xlsx`,
      );
      return res.data.data;
    },
  });
}

// ── List PDF versions ──
export interface ProformaVersionDTO {
  id: string;
  version: number;
  download_url: string;
  generated_at: string;
  generated_by: { id: string; nombre: string };
}

export function useProformaVersions(proformaId: string) {
  return useQuery({
    queryKey: ['proforma-versions', proformaId] as const,
    queryFn: async () => {
      const response = await api.get<{ data: ProformaVersionDTO[] }>(
        `/proformas/${proformaId}/versions`,
      );
      return response.data.data;
    },
    enabled: !!proformaId,
  });
}

// ── Copy proforma ──
export function useCopyProforma() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      proformaId,
      data,
    }: {
      proformaId: string;
      data: { targetProjectId: string; copyCondiciones: boolean };
    }) => {
      const response = await api.post<{
        data: { id: string; project_id: string; numero: number; estado: string; items_copied: number; created_at: string };
      }>(`/proformas/${proformaId}/copy`, data);
      return response.data.data;
    },
    onSuccess: (_data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.all });
      toast('success', `Proforma copiada con ${_data.items_copied} items`);
    },
  });
}

// ── Impact analysis (T-021) ──
export interface ProformaImpactDTO {
  has_ot: boolean;
  ot_items_count: number;
  has_planillas: boolean;
  planillas_count: number;
  has_approved_planillas: boolean;
  approved_planillas_count: number;
  has_pedidos: boolean;
  pedidos_count: number;
  affected_modules: string[];
  warnings: string[];
}

export function useProformaImpact(proformaId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['proforma-impact', proformaId] as const,
    queryFn: async () => {
      const response = await api.get<{ data: ProformaImpactDTO }>(
        `/proformas/${proformaId}/impact`,
      );
      return response.data.data;
    },
    enabled: !!proformaId && enabled,
  });
}

// ── Proforma Fotos ──

export interface ProformaFotoDTO {
  id: string;
  s3_key: string;
  filename: string;
  mime_type: string;
  created_at: string;
}

export function useProformaFotos(proformaId: string | undefined) {
  return useQuery({
    queryKey: ['proforma-fotos', proformaId] as const,
    queryFn: async () => {
      const res = await api.get<{ data: ProformaFotoDTO[] }>(`/proformas/${proformaId}/fotos`);
      return res.data.data;
    },
    enabled: !!proformaId,
  });
}

export function useCreateProformaFoto() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { proformaId: string; data: { s3Key: string; filename: string; mimeType: string } }) => {
      const res = await api.post<{ data: ProformaFotoDTO }>(`/proformas/${vars.proformaId}/fotos`, vars.data);
      return res.data.data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['proforma-fotos', vars.proformaId] });
    },
  });
}

// ── Sync recipe prices (borrador only) ──
export function useSyncRecipePrices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (proformaId: string) => {
      const response = await api.post<{ data: { updated: number } }>(
        `/proformas/${proformaId}/sync-recipe-prices`,
      );
      return response.data.data;
    },
    onSuccess: (data, proformaId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.detail(proformaId) });
      toast('success', `Precios actualizados en ${data.updated} ítem(s)`);
    },
  });
}

// ── Sync new medidas items into proforma (borrador only) ──
export function useSyncMedidasToProforma() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (proformaId: string) => {
      const response = await api.post<{ data: { added: number } }>(
        `/proformas/${proformaId}/sync-medidas`,
      );
      return response.data.data;
    },
    onSuccess: (data, proformaId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.detail(proformaId) });
      if (data.added > 0) {
        toast('success', `${data.added} ítem(s) nuevos agregados desde medidas`);
      } else {
        toast('info', 'No hay ítems nuevos en medidas para agregar');
      }
    },
  });
}

// ── Delete proforma (hard, borrador only) ──
export function useDeleteProforma() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/proformas/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.proformas.all });
      toast('success', 'Proforma eliminada');
    },
  });
}

// Re-export DTO types for convenience
export type { ProformaListDTO, ProformaDetailDTO, ProformaItemDTO, ProformaTotals } from '../../../backend/services/proforma-service';
